import { getOriginalTeacherIssueLabel } from '@airreading/common';
import {
  CoachSessionRemarkV2,
  OriginalTeacherIssue,
  OriginalTeacherIssueSelectable,
  originalTeacherIssueSelectableValues,
} from '@airreading/common/data';

const orderedSubRequestIssues: OriginalTeacherIssue[] = [
  OriginalTeacherIssue.short_notice_sub_request,
  OriginalTeacherIssue.regular_sub_request,
  OriginalTeacherIssue.short_notice_sub_request_excused,
  OriginalTeacherIssue.regular_sub_request_excused,
];

const subRequestIssues = new Set<OriginalTeacherIssue>(orderedSubRequestIssues);

const firstSubRequestIssue = orderedSubRequestIssues[0];

const normalizeIssue = (
  issue: OriginalTeacherIssueSelectable | OriginalTeacherIssue | null | undefined,
): OriginalTeacherIssueSelectable => issue ?? 'null';

export const orderedOriginalTeacherIssueOptions: OriginalTeacherIssueSelectable[] = [
  ...originalTeacherIssueSelectableValues.filter((issue) => issue === 'null' || !subRequestIssues.has(issue)),
  ...orderedSubRequestIssues,
];

export const isSubRequestIssue = (
  issue: OriginalTeacherIssueSelectable | OriginalTeacherIssue | null | undefined,
): boolean => {
  const normalizedIssue = normalizeIssue(issue);
  return normalizedIssue !== 'null' && subRequestIssues.has(normalizedIssue);
};

/** Show Reason whenever any Original teacher issue tag is set (sub request, TNS, or TIT). */
export const shouldShowReasonField = (
  issue: OriginalTeacherIssueSelectable | OriginalTeacherIssue | null | undefined,
): boolean => {
  const normalizedIssue = normalizeIssue(issue);
  return normalizedIssue !== 'null';
};

export const getOriginalTeacherIssueDropdownText = (
  issue: OriginalTeacherIssueSelectable | OriginalTeacherIssue | null | undefined,
): string => {
  const normalizedIssue = normalizeIssue(issue);
  switch (normalizedIssue) {
    case OriginalTeacherIssue.short_notice_sub_request:
      return 'Short notice sub request';
    case OriginalTeacherIssue.regular_sub_request:
      return 'Regular sub request';
    case OriginalTeacherIssue.short_notice_sub_request_excused:
      return 'Short notice sub request (excused)';
    case OriginalTeacherIssue.regular_sub_request_excused:
      return 'Regular sub request (excused)';
    default:
      return getOriginalTeacherIssueLabel(normalizedIssue).replace(': ', ':').replace(' (end early)', '(end early)');
  }
};

export const getOriginalTeacherIssueOptionHint = (
  issue: OriginalTeacherIssueSelectable | OriginalTeacherIssue | null | undefined,
): string | null => (isSubRequestIssue(issue) ? 'Reason required' : null);

export const mapIssueFromSessionRemarks = (selectedRemarks: CoachSessionRemarkV2[]): OriginalTeacherIssueSelectable => {
  if (selectedRemarks.includes(CoachSessionRemarkV2.subClassWithin12Hours)) {
    return OriginalTeacherIssue.short_notice_sub_request;
  }
  if (selectedRemarks.includes(CoachSessionRemarkV2.subClass)) {
    return OriginalTeacherIssue.regular_sub_request;
  }
  return 'null';
};

export const getOriginalTeacherIssueOptionClassName = (
  issue: OriginalTeacherIssueSelectable | OriginalTeacherIssue | null | undefined,
): string | undefined => {
  const normalizedIssue = normalizeIssue(issue);
  return normalizedIssue === firstSubRequestIssue ? 'mt-2 border-t border-muted pt-3' : undefined;
};
